import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { AiGameSession } from '@/types';

interface OngoingGamesProps {
  refreshKey?: number;
}

const OngoingGames = ({ refreshKey }: OngoingGamesProps) => {
  const { user } = useAuthStore();
  const [aiGames, setAiGames] = useState<AiGameSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOngoingGames = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!user?.uid) {
        throw new Error('User not authenticated');
      }

      // Get all AI game sessions created by this teacher
      const games = await authService.getTeacherAiGameSessions(user.uid);

      // Filter to only show active (non-expired) games
      const now = new Date();
      const activeGames = games.filter(
        game => game.status === 'active' && new Date(game.endDate) > now
      );

      // Sort by most recent first
      activeGames.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      console.log(`✅ Found ${activeGames.length} active games out of ${games.length} total`);
      setAiGames(activeGames);
    } catch (err: unknown) {
      console.error('❌ Failed to load ongoing games:', err);
      setError(err instanceof Error ? err.message : 'Failed to load ongoing games');
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (user?.uid) {
      loadOngoingGames();
    }
  }, [user?.uid, refreshKey, loadOngoingGames]);

  const handleCopyGameCode = async (gameCode: string) => {
    try {
      await navigator.clipboard.writeText(gameCode);
      alert('Game code copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy game code:', err);
      alert('Failed to copy game code');
    }
  };

  const handleEndGame = async (gameId: string) => {
    if (!confirm('Are you sure you want to end this game? Students will no longer be able to join.')) {
      return;
    }

    try {
      await authService.cancelAiGameSession(gameId);
      // Reload the games list
      loadOngoingGames();
    } catch (err: unknown) {
      console.error('❌ Failed to end game:', err);
      alert('Failed to end game: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const formatTimeRemaining = (endDate: Date) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();

    if (diff < 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    } else if (hours > 0) {
      return `${hours}h remaining`;
    } else {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${minutes}m remaining`;
    }
  };

  if (isLoading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '3rem',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p style={{ color: '#64748b' }}>Loading ongoing games...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '3rem',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❌</div>
        <p style={{ color: '#dc2626' }}>{error}</p>
        <button
          onClick={loadOngoingGames}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (aiGames.length === 0) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '3rem',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎮</div>
        <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>No Ongoing Games</h3>
        <p style={{ color: '#64748b', marginBottom: '0' }}>
          Create an AI game to get started!
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '24px',
      padding: '2rem',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{
          color: '#374151',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '1.3rem'
        }}>
          🎮 Ongoing AI Games
        </h3>
        <p style={{ color: '#64748b', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
          Active games available for students to play
        </p>
      </div>

      {/* Games Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '1.5rem'
      }}>
        {aiGames.map((game) => (
          <div
            key={game.id}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.5rem',
              border: '2px solid rgba(249, 115, 22, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.borderColor = '#f97316';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(249, 115, 22, 0.2)';
            }}
          >
            {/* Game Code Badge */}
            <div style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              <div style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.75rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '0.25rem'
              }}>
                Game Code
              </div>
              <div style={{
                color: 'white',
                fontSize: '2rem',
                fontWeight: 'bold',
                letterSpacing: '0.3rem',
                fontFamily: 'monospace'
              }}>
                {game.gameCode}
              </div>
            </div>

            {/* Game Info */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {game.questionSetName}
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                fontSize: '0.85rem',
                color: '#64748b'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>👥</span>
                  <span>
                    <strong>{game.totalPlays || 0}</strong> {game.totalPlays === 1 ? 'play' : 'plays'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>🤖</span>
                  <span>
                    {game.allowStudentChoice
                      ? 'Students choose AI count'
                      : `${game.fixedAiPlayerCount} AI opponents (fixed)`
                    }
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>⏱️</span>
                  <span>{formatTimeRemaining(game.endDate)}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>📅</span>
                  <span>Created {formatDate(game.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => handleCopyGameCode(game.gameCode)}
                style={{
                  flex: 1,
                  padding: '0.65rem',
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                📋 Copy Code
              </button>
              <button
                onClick={() => handleEndGame(game.id)}
                style={{
                  padding: '0.65rem',
                  background: 'white',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#fecaca';
                }}
                title="End this game"
              >
                🚫
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OngoingGames;
